/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import fastMemoize from 'fast-memoize';
import { FormikProps } from 'formik';
import Fuse from 'fuse.js';
import humanize from 'humanize-plus';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Error as ConflictIcon } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Tooltip,
} from '@mui/material';

import { AdvancedSticky } from '../../../../../../../../components/AdvancedSticky/AdvancedSticky';
import { BoxPicker } from '../../../../../../../../components/BoxPicker/BoxPicker';
import { DatasetCard } from '../../../../../../../../components/DatasetCard/DatasetCard';
import { DataSetCategoriesList } from '../../../../../../../../components/DataSetCategoriesList/DataSetCategoriesList';
import { HUSpin } from '../../../../../../../../components/HUSpin/HUSpin';
import { ArrowRoundedIcon } from '../../../../../../../../components/svg-icons/ArrowRoundedIcon';
import {
  CLUSTERGW_DEFAULT_DATABASE,
  DATASET_DEFAULT_READONLY,
  IMPORT_CLUSTER_DATA_SETS_OVERLAY_CSS_ANCHOR,
} from '../../../../../../../../constants/settings';
import { HUAutocomplete } from '../../../../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../../../../i18n';
import { useSelectClusterDataSetsStyles } from '../../../../../../../../styles/selectClusterDataSets';
import { EntityId } from '../../../../../../../../types/api/EntityId';
import { ClusterAccessor } from '../../../../../../../../types/common/Accessors';
import { Dataset } from '../../../../../../../../types/dataset/Dataset';
import { DatasetCategory } from '../../../../../../../../types/dataset/DatasetCategory';
import { DatasetImportStatus } from '../../../../../../../../types/dataset/DatasetImportStatus';
import { getDatasetImportsWithStatus } from '../../../../../../../../utils/getDatasetImportsWithStatus';
import { fetchClusterDatabaseSchemas, fetchDatasetImports } from '../../../../../../../../utils/queryFns';
import { initialImportDatasetMeta } from '../../ImportDataSetsCluster.consts';
import { ImportDataSetsClusterValues } from '../../ImportDataSetsCluster.types';
import { CategoriesCountsTypes } from './ImportDataSetsClusterForm.types';

type ImportDatasetsOverlayDatasetsProps<Values> = FormikProps<Values> & {
  dataSetsCategories: DatasetCategory[];
  isDatabasesQueryFetching: boolean;
  onDatabaseDropdownFocus: () => any;
  databases: string[];
  clusterAccessor: ClusterAccessor;
  isDisabledCancelButton: boolean;
  isDisabledSubmitButton: boolean;
  onCancel: () => void;
};

const fuseOptions = {
  ignoreLocation: true,
  threshold: 0,
};

export function ImportDataSetsClusterForm<Values extends ImportDataSetsClusterValues>(
  props: ImportDatasetsOverlayDatasetsProps<Values>,
) {
  const {
    values,
    setFieldValue,
    dataSetsCategories,
    setFieldTouched,
    handleChange,
    isDatabasesQueryFetching,
    databases,
    onDatabaseDropdownFocus,
    clusterAccessor,
    isDisabledCancelButton,
    isDisabledSubmitButton,
    submitForm,
    onCancel,
  } = props;
  const { t } = useTranslation([Namespaces.General, Namespaces.Common]);
  const classes = useSelectClusterDataSetsStyles();
  const [query, setQuery] = useState<string>('');
  const { selectedDataSetCategoryId, selectedDataSetIds, dataSetsMetas, database, storageAvailable } = values;

  const { selectedFilteredCategory, selectedCategory, filteredDataSetsCategories } = useMemo<{
    filteredDataSetsCategories: DatasetCategory[];
    selectedFilteredCategory?: DatasetCategory;
    selectedCategory?: DatasetCategory;
  }>(() => {
    const filteredDataSetsCategories = dataSetsCategories.map(c => ({
      ...c,
      datasets: query
        ? new Fuse(c.datasets, {
            ...fuseOptions,
            keys: ['name'],
          })
            .search(query)
            .map(v => v.item)
        : c.datasets,
    }));
    const selectedCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
    const selectedFilteredCategory = filteredDataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
    return {
      filteredDataSetsCategories,
      selectedCategory,
      selectedFilteredCategory,
    };
  }, [dataSetsCategories, query, selectedDataSetCategoryId]);

  const dataSets = useMemo(() => selectedFilteredCategory?.datasets ?? [], [selectedFilteredCategory]);

  const databaseSchemasQuery = useQuery(
    [fetchClusterDatabaseSchemas.key, { ...clusterAccessor, databaseName: values.database }],
    fetchClusterDatabaseSchemas.fn,
  );

  const datasetImportsQuery = useQuery([fetchDatasetImports.key, clusterAccessor], fetchDatasetImports.fn, {
    refetchOnWindowFocus: false,
  });

  const databaseSchemas = useMemo(() => databaseSchemasQuery.data?.payload ?? [], [databaseSchemasQuery.data]);

  const databaseDatasetImports = useMemo(
    () => (datasetImportsQuery.data?.payload ?? []).filter(v => v.database === database),
    [database, datasetImportsQuery.data],
  );

  const dataSetsInPendingState = useMemo(() => {
    return getDatasetImportsWithStatus(databaseDatasetImports, [DatasetImportStatus.Pending]).map(v => v.dataset.id);
  }, [databaseDatasetImports]);

  const dataSetsInImportingState = useMemo(() => {
    return getDatasetImportsWithStatus(databaseDatasetImports, [DatasetImportStatus.Importing]).map(v => v.dataset.id);
  }, [databaseDatasetImports]);

  const everImportedDataSetsIds = useMemo(() => {
    return databaseDatasetImports.map(v => v.dataset.id);
  }, [databaseDatasetImports]);

  useEffect(() => {
    if (databases.indexOf(database) === -1) {
      void setFieldValue(
        'database',
        databases.indexOf(CLUSTERGW_DEFAULT_DATABASE) > -1 ? CLUSTERGW_DEFAULT_DATABASE : databases[0],
      );
    }
  }, [database, databases, setFieldValue]);

  useEffect(() => {
    if (dataSetsCategories.length) {
      if (!selectedCategory && selectedDataSetCategoryId) {
        const currentDataSetCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
        if (currentDataSetCategory) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        } else {
          void setFieldValue('selectedDataSetCategoryId', undefined);
        }
      } else if (!selectedCategory && !selectedDataSetCategoryId) {
        const currentDataSetCategory = dataSetsCategories[0];
        if (currentDataSetCategory) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        }
      } else {
        const currentDataSetCategory = dataSetsCategories.find(i => i.id === selectedDataSetCategoryId);
        if (currentDataSetCategory && currentDataSetCategory.id !== selectedCategory?.id) {
          void setFieldValue('selectedDataSetCategoryId', currentDataSetCategory.id);
        }
      }
    }
  }, [dataSetsCategories, selectedCategory, selectedDataSetCategoryId, setFieldValue]);

  const allDataSets = useMemo(() => {
    const result: Dataset[] = [];
    dataSetsCategories.forEach(cat => {
      cat.datasets.forEach(set => {
        result.push(set);
      });
    });
    return result;
  }, [dataSetsCategories]);

  useEffect(() => {
    const nextMetas: ImportDataSetsClusterValues['dataSetsMetas'] = {
      ...values.dataSetsMetas,
    };
    let shouldUpdate = false;
    allDataSets.forEach(dataSet => {
      if (!dataSetsMetas[dataSet.id]) {
        shouldUpdate = true;
        nextMetas[dataSet.id] = { ...initialImportDatasetMeta };
      }
    });
    if (shouldUpdate) {
      void setFieldValue('dataSetsMetas', nextMetas);
    }
  }, [allDataSets, dataSetsMetas, setFieldValue, values.dataSetsMetas]);

  const getSelectedDataSets = useCallback(
    (selectedIds: EntityId[]): Dataset[] => {
      const result: Dataset[] = [];
      allDataSets.forEach(set => {
        if (selectedIds.includes(set.id)) {
          result.push(set);
        }
      });
      return result;
    },
    [allDataSets],
  );

  const selectedDataSets = useMemo(() => {
    return getSelectedDataSets(selectedDataSetIds);
  }, [getSelectedDataSets, selectedDataSetIds]);

  useEffect(() => {
    const nextSelectedDataSetIds: string[] = [];
    let shouldSetNextValue = false;
    selectedDataSets.forEach(set => {
      if (databaseSchemas.indexOf(set.schema) > -1) {
        shouldSetNextValue = true;
        return;
      }
      nextSelectedDataSetIds.push(set.id);
    });
    if (shouldSetNextValue) {
      void setFieldValue('selectedDataSetIds', nextSelectedDataSetIds);
    }
  }, [databaseSchemas, selectedDataSets, setFieldValue]);

  const categoriesCounts = useMemo<CategoriesCountsTypes>(() => {
    const result: CategoriesCountsTypes = {};
    dataSetsCategories.forEach(cat => {
      let count = 0;
      cat.datasets.forEach(set => {
        if (selectedDataSetIds.includes(set.id)) {
          count++;
        }
      });
      result[cat.id] = [count, filteredDataSetsCategories.find(v => v.id === cat.id)?.datasets.length ?? 0];
    });
    return result;
  }, [dataSetsCategories, filteredDataSetsCategories, selectedDataSetIds]);

  const isLoadingSchemas = useMemo(() => databaseSchemasQuery.status === 'loading', [databaseSchemasQuery.status]);

  const {
    selectedSetsFinalSizeHumanized,
    accessibleSizeHumanized,
    accessibleSizeMBytes,
    availableSizeHumanized,
    selectedSetsImportSizeHumanized,
  } = useMemo(() => {
    const selectedSetsFinalSize =
      selectedDataSets.reduce((prev, current) => prev + +current.finalSizeMb, 0) * Math.pow(1024, 2);

    const selectedSetsImportSize =
      selectedDataSets.reduce((prev, current) => prev + +current.importSizeMb, 0) * Math.pow(1024, 2);

    const availableSize = storageAvailable * Math.pow(1024, 2);
    const availableSizeMBytes = availableSize / Math.pow(1024, 2);

    const accessibleSize = Math.max(0, availableSize - selectedSetsImportSize);
    const accessibleSizeMBytes = accessibleSize / Math.pow(1024, 2);

    return {
      selectedSetsFinalSize,
      selectedSetsFinalSizeHumanized: humanize.fileSize(selectedSetsFinalSize),
      selectedSetsImportSize,
      selectedSetsImportSizeHumanized: humanize.fileSize(selectedSetsImportSize),

      accessibleSize,
      accessibleSizeMBytes,
      accessibleSizeHumanized: humanize.fileSize(accessibleSize),
      availableSize,
      availableSizeMBytes,
      availableSizeHumanized: humanize.fileSize(availableSize),
    };
  }, [selectedDataSets, storageAvailable]);

  const getDataSetStatusData = useMemo(
    () =>
      fastMemoize(
        (
          dataSet: Dataset,
        ): {
          disabled: boolean;
          disableReason:
            | 'dataset_already_queued'
            | 'dataset_already_importing'
            | 'dataset_already_imported'
            | 'insufficient_space'
            | 'schema_conflict'
            | '';
        } => {
          if (dataSetsInPendingState.includes(dataSet.id)) {
            return {
              disabled: true,
              disableReason: 'dataset_already_queued',
            };
          }
          if (dataSetsInImportingState.includes(dataSet.id)) {
            return {
              disabled: true,
              disableReason: 'dataset_already_importing',
            };
          }
          if (everImportedDataSetsIds.includes(dataSet.id)) {
            return {
              disabled: true,
              disableReason: 'dataset_already_imported',
            };
          }
          if (databaseSchemas.indexOf(dataSet.schema) > -1) {
            return {
              disabled: true,
              disableReason: 'schema_conflict',
            };
          }
          if (+dataSet.importSizeMb > accessibleSizeMBytes && values.selectedDataSetIds.indexOf(dataSet.id) === -1) {
            return {
              disabled: true,
              disableReason: 'insufficient_space',
            };
          }
          return {
            disabled: false,
            disableReason: '',
          };
        },
      ),
    [
      dataSetsInPendingState,
      dataSetsInImportingState,
      everImportedDataSetsIds,
      databaseSchemas,
      accessibleSizeMBytes,
      values.selectedDataSetIds,
    ],
  );

  return (
    <>
      <div className={classes.filtersContainer}>
        <div className={classes.filterControl}>
          <FormControl className={classes.w100} variant="outlined">
            <InputLabel htmlFor="database">{t('data_sets.database')}</InputLabel>
            <HUAutocomplete
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="database"
                  placeholder={t('data_sets.database')}
                  label={t('data_sets.database')}
                  endAdornment={
                    <>
                      {isDatabasesQueryFetching && <CircularProgress color="primary" size={20} disableShrink key="1" />}
                      {renderParams.InputProps.endAdornment}
                    </>
                  }
                />
              )}
              disableClearable={true}
              options={databases}
              value={database}
              openOnFocus={true}
              onOpen={onDatabaseDropdownFocus}
              onChange={(_e: any, value: string | null) => {
                if (value !== null) {
                  void setFieldValue('database', value, true);
                  void setFieldTouched('database', true);
                }
              }}
              popupIcon={<ArrowRoundedIcon />}
            />
          </FormControl>
        </div>
        <div className={classes.filterControlContainer}>
          <div className={cx(classes.filterControlLabel, classes.ml10)}>{t('data_sets.search')}</div>
          <div className={cx(classes.filterControl, classes.ml10)}>
            <FormControl className={classes.w100} variant="outlined">
              <InputLabel htmlFor="dataset_name">{t('data_sets.dataset_name')}</InputLabel>
              <OutlinedInput
                id="dataset_name"
                name="dataset_name"
                label={t('data_sets.dataset_name')}
                fullWidth
                type="text"
                value={query}
                onChange={({ target }) => setQuery(target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="search"
                      onClick={() => setQuery('')}
                      onMouseDown={() => setQuery('')}
                      edge="end"
                      size="large"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </div>
        </div>
      </div>
      <HUSpin spinning={isLoadingSchemas} containerClassName={classes.dataSetsStepContainer}>
        <div className={classes.dataSetsCategoriesContainer}>
          <div className={classes.dataSetsCategoriesContainerStickyWrapper}>
            <AdvancedSticky
              scrollElement={`.${IMPORT_CLUSTER_DATA_SETS_OVERLAY_CSS_ANCHOR}`}
              topOffset={70}
              bottomOffset={66}
            >
              {({ elementRef, style }) => (
                <div ref={elementRef} style={style}>
                  <div className={classes.t}>{t('data_sets.categories_list_title')}</div>
                  <DataSetCategoriesList
                    options={filteredDataSetsCategories}
                    selected={selectedFilteredCategory}
                    onItemClick={(_e, v) => setFieldValue('selectedDataSetCategoryId', v.id)}
                    getOptionLabel={o => o.i18n.title}
                    getOptionThumbnail={o => o.thumbnail}
                    getOptionCount={option => categoriesCounts[option.id]}
                    getOptionSelected={option => option.id === selectedCategory?.id}
                  />
                </div>
              )}
            </AdvancedSticky>
          </div>
          <div className={classes.controlButtons}>
            <Button
              onClick={onCancel}
              variant="outlined"
              disabled={isDisabledCancelButton}
              key="cancel_import_datasets"
            >
              {t(`${Namespaces.Cluster}:details.datasets.overlay.buttons.cancel`)}
            </Button>
            <Button
              onClick={submitForm}
              variant="contained"
              disabled={isDisabledSubmitButton}
              key="submit_import_datasets"
            >
              {t(`${Namespaces.Cluster}:details.datasets.overlay.buttons.import`)}
            </Button>
          </div>
        </div>
        <div className={classes.dataSetsGridContainer}>
          <AdvancedSticky scrollElement={`.${IMPORT_CLUSTER_DATA_SETS_OVERLAY_CSS_ANCHOR}`} topOffset={70}>
            {({ elementRef, style }) => (
              <div ref={elementRef} style={style}>
                <div className={classes.t}>{t('data_sets.categories_grid_title')}</div>
                {dataSets.length > 0 ? (
                  <div className={classes.dataSetsGrid}>
                    <BoxPicker
                      selected={selectedDataSetIds}
                      onChange={value => setFieldValue('selectedDataSetIds', value)}
                      multiple
                    >
                      {dataSets.map(dataSet => {
                        const status = getDataSetStatusData(dataSet);
                        return (
                          <Tooltip
                            title={
                              status.disabled
                                ? t(`data_sets.disable_reasons.${status.disableReason}`, {
                                    databaseName: values.database,
                                    schemaName: dataSet.schema,
                                  })!
                                : ''
                            }
                            key={dataSet.id}
                          >
                            <BoxPicker.Item
                              value={dataSet.id}
                              disabled={status.disabled || isLoadingSchemas}
                              className={classes.cardItem}
                            >
                              <>
                                <DatasetCard
                                  dataSet={dataSet}
                                  className={cx(classes.disablableBox, {
                                    [classes.disabled]: status.disabled || isLoadingSchemas,
                                  })}
                                  renderToLower={() => (
                                    <Tooltip
                                      title={status.disabled ? '' : t('data_sets.hints.readonly_meaning')!}
                                      placement="left"
                                    >
                                      <FormControlLabel
                                        className={classes.readonlyCheckboxLabel}
                                        name={`dataSetsMetas.${dataSet.id}.readonly`}
                                        checked={dataSetsMetas[dataSet.id]?.readonly}
                                        defaultChecked={DATASET_DEFAULT_READONLY}
                                        onClick={event => {
                                          event.stopPropagation();
                                        }}
                                        onChange={handleChange}
                                        control={
                                          <Checkbox
                                            color="primary"
                                            size="small"
                                            disableRipple
                                            className={classes.readonlyCheckbox}
                                          />
                                        }
                                        label={t('data_sets.readonly')}
                                        labelPlacement="end"
                                        disabled={status.disabled}
                                      />
                                    </Tooltip>
                                  )}
                                />
                                {status.disableReason === 'schema_conflict' && (
                                  <ConflictIcon className={classes.cardItemErrorIcon} />
                                )}
                              </>
                            </BoxPicker.Item>
                          </Tooltip>
                        );
                      })}
                    </BoxPicker>
                  </div>
                ) : (
                  <div className={classes.noDataSetsText}>{t('data_sets.no_datasets_in_category')}</div>
                )}
                <div className={classes.divider} />
                <div className={classes.dataSetsSummaryStickyContainer}>
                  <div className={classes.dataSetsSummary}>
                    <span>
                      {t('data_sets.summary.dataset_selection', {
                        count: selectedDataSets.length,
                      })}
                    </span>
                    <span>
                      {t('data_sets.summary.size_info', {
                        total: selectedSetsFinalSizeHumanized,
                        totalImport: selectedSetsImportSizeHumanized,
                        accessible: accessibleSizeHumanized,
                        available: availableSizeHumanized,
                      })}
                    </span>
                  </div>
                  <div className={classes.dataSetsBottomHint}>{t('data_sets.long_import_hint')}</div>
                </div>
              </div>
            )}
          </AdvancedSticky>
        </div>
      </HUSpin>
    </>
  );
}
